<script lang="ts">
	import { readSettings } from '$lib/settings';
	import { sound, unlockAudio } from '$lib/sound';
	import { THEME_VIDEOS, THEME_PREVIEWS } from '$lib/themes';
	import LogoFull from './LogoFull.svelte';

	const GAME_TITLES: Record<string, string> = {
		'/word-guess': 'Word Guess',
		'/word-group': 'Word Group',
		'/word-build': 'Word Build',
		'/word-cross': 'Word Cross S',
		'/word-cross-m': 'Word Cross M',
		'/word-find': 'Word Find',
		'/word-crypto': 'Word Crypto',
		'/word-speed': 'Word Speed',
		'/num-fill': 'Num Fill',
		'/word-tower': 'Word Tower'
	};

	const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

	let isCoop = false;
	let isArchive = false;
	let coopGameTitle = '';
	let archiveGameTitle = '';
	let archiveDateLabel = '';

	if (typeof window !== 'undefined') {
		try {
			const url = new URL(window.location.href);
			isCoop = url.searchParams.has('coop');
			coopGameTitle = GAME_TITLES[url.pathname] || '';
			const dateParam = url.searchParams.get('date');
			if (dateParam && !isCoop) {
				isArchive = true;
				archiveGameTitle = GAME_TITLES[url.pathname] || 'Game';
				const parts = dateParam.split('-').map(Number);
				if (parts.length === 3) {
					archiveDateLabel = `${MONTHS[parts[1] - 1]} ${parts[2]}, ${parts[0]}`;
				}
			}
		} catch {
			/* ignore */
		}
	}

	const SESSION_FLAG = 'bubble-splash-shown';
	const alreadyShown =
		typeof window !== 'undefined' && sessionStorage.getItem(SESSION_FLAG) === '1';

	let visible = $state(!alreadyShown);

	$effect(() => {
		if (visible) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => { document.body.style.overflow = ''; };
	});
	let fading = $state(false);
	let progress = $state(0);
	let started = $state(false);
	let loadingText = $state(
		isCoop ? `Loading ${coopGameTitle || 'co-op'}…` :
		isArchive ? `Loading ${archiveGameTitle}…` :
		'Initializing...'
	);

	const THEME_OVERLAYS: Record<string, string> = {
		hearth: 'rgba(120,60,0,0.3)',
		galactic: 'rgba(0,0,0,0.6)',
		wizardry: 'rgba(10,5,30,0.5)',
		overgrown: 'rgba(5,20,5,0.6)',
		outbreak: 'rgba(15,12,8,0.6)',
		catchers: 'rgba(10,30,80,0.45)',
		noir: 'rgba(0,0,0,0.4)',
		dark: 'rgba(0,0,0,1)',
		light: 'rgba(245,245,245,1)'
	};
	const _settings = readSettings();
	const previewSrc = THEME_VIDEOS[_settings.theme] ?? THEME_VIDEOS['hearth'];
	const posterSrc = THEME_PREVIEWS[_settings.theme] ?? THEME_PREVIEWS['hearth'];
	const overlayColor = THEME_OVERLAYS[_settings.theme] ?? THEME_OVERLAYS['hearth'];

	function startLoading() {
		const p1 = 600; // 0% → 25%
		const h1 = 500; // hang at 25%
		const p2 = 700; // 25% → 50%
		const h2 = 600; // hang at 50%
		const p3 = 800; // 50% → 80%
		const h3 = 700; // hang at 80%
		const p4 = 900; // 80% → 100%

		const c1 = p1;
		const c2 = c1 + h1;
		const c3 = c2 + p2;
		const c4 = c3 + h2;
		const c5 = c4 + p3;
		const c6 = c5 + h3;

		const start = performance.now();

		let m25 = false,
			m50 = false,
			m80 = false,
			m100 = false;

		function easeOut(t: number) {
			return 1 - Math.pow(1 - t, 2);
		}

		function step(now: number) {
			const e = now - start;

			if (e < c1) {
				progress = Math.round(easeOut(e / p1) * 25);
			} else if (e < c2) {
				progress = 25;
			} else if (e < c3) {
				progress = Math.round(25 + easeOut((e - c2) / p2) * 25);
			} else if (e < c4) {
				progress = 50;
			} else if (e < c5) {
				progress = Math.round(50 + easeOut((e - c4) / p3) * 30);
			} else if (e < c6) {
				progress = 80;
			} else {
				const t = Math.min((e - c6) / p4, 1);
				progress = Math.round(80 + easeOut(t) * 20);
				if (t >= 1) {
					progress = 100;
					if (!m100) {
						m100 = true;
						sound.win();
					}
					setTimeout(() => {
						fading = true;
						setTimeout(() => {
							visible = false;
							try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch {}
							try { window.dispatchEvent(new Event('splash-done')); } catch {}
						}, 450);
					}, 300);
					return;
				}
			}

			if (progress >= 25 && !m25) {
				m25 = true;
				sound.connect(0);
				loadingText = isCoop ? 'Joining room…' : isArchive ? `Restoring ${archiveDateLabel}…` : 'Loading games...';
			}
			if (progress >= 50 && !m50) {
				m50 = true;
				sound.connect(1);
				loadingText = isCoop ? 'Syncing with friend…' : isArchive ? 'Loading saved state…' : 'Loading assets...';
			}
			if (progress >= 80 && !m80) {
				m80 = true;
				sound.connect(2);
				loadingText = isCoop ? 'Almost ready…' : isArchive ? 'Almost ready…' : 'Almost ready...';
			}
			if (progress >= 100 && m100) {
				loadingText = 'Ready!';
			}

			requestAnimationFrame(step);
		}

		requestAnimationFrame(step);
	}

	function handleTap() {
		if (started) return;
		started = true;
		// Start the loader immediately so the tap always registers visually.
		// Audio unlocking happens independently via the global pointerup listener
		// in sound.ts (queued sounds flush once the context resumes), so we must
		// NOT await it here — on mobile webviews the AudioContext often stays
		// suspended until a later gesture, which previously left the splash stuck
		// with `started = true` and required a second tap to proceed.
		startLoading();
		void unlockAudio();
	}
</script>

{#if visible}
	<div role="button" tabindex="0" aria-label="Tap to start" class="fixed inset-0 z-9999 flex cursor-pointer items-center justify-center transition-opacity duration-450" style="background: var(--splash-bg, #1a0a00);" class:opacity-0={fading} onpointerdown={handleTap} onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && handleTap()}>
		{#if _settings.theme === 'dark'}
			<div class="absolute inset-0" style="background: #0f0f0f;"></div>
			<div class="pointer-events-none absolute inset-0" style="background-image: linear-gradient(to right, transparent 0 24px, rgba(239, 68, 68, 0.18) 24px 25px, transparent 25px), repeating-linear-gradient(to bottom, transparent 0 27px, rgba(147, 197, 253, 0.05) 27px 28px);"></div>
		{:else if _settings.theme === 'light'}
			<div class="absolute inset-0" style="background: #faf6ec;"></div>
			<div class="pointer-events-none absolute inset-0" style="background-image: linear-gradient(to right, transparent 0 24px, rgba(239, 68, 68, 0.28) 24px 25px, transparent 25px), repeating-linear-gradient(to bottom, transparent 0 27px, rgba(59, 130, 246, 0.10) 27px 28px);"></div>
		{:else}
			<video src={previewSrc} poster={posterSrc} autoplay loop muted playsinline class="absolute inset-0 h-full w-full object-cover"></video>
			<div class="absolute inset-0" style="background: {overlayColor}; background-image: radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%);"></div>
		{/if}
		{#if _settings.theme === 'noir'}
			<div class="noir-grain pointer-events-none absolute inset-0"></div>
			<div class="noir-scratches pointer-events-none absolute inset-0"></div>
			<div class="noir-dust pointer-events-none absolute inset-0"></div>
			<div class="noir-burn pointer-events-none absolute inset-0"></div>
		{/if}
		{#if _settings.theme === 'overgrown'}
			<div class="overgrown-grunge pointer-events-none absolute inset-0"></div>
			<div class="overgrown-burn pointer-events-none absolute inset-0"></div>
		{/if}
		{#if _settings.theme === 'wizardry'}
			<div class="wizardry-burn pointer-events-none absolute inset-0"></div>
		{/if}
		{#if _settings.theme === 'outbreak'}
			<div class="outbreak-grunge pointer-events-none absolute inset-0"></div>
			<div class="outbreak-noise pointer-events-none absolute inset-0"></div>
			<div class="outbreak-burn pointer-events-none absolute inset-0"></div>
		{/if}

		<div class="relative flex flex-col items-center gap-5">
			<LogoFull size={56} />

			{#if started}
				<div class="pixel-frame relative h-5 w-56 animate-[fade-up_500ms_350ms_ease_both]">
					<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
					<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
					<div class="pixel-frame absolute inset-1 bg-(--bottom-nav-frame-shadow)"></div>
					<div class="pixel-frame absolute inset-1.5 bg-(--bottom-nav-frame-fill)"></div>
					<div class="absolute inset-1.5 overflow-hidden">
						<div class="loading-fill fill-bar pixel-frame-flat-left absolute inset-y-0 left-0 overflow-hidden" style:width="{progress}%">
							<div class="glint"></div>
						</div>
					</div>
				</div>
				<p class="text-center text-xl font-bold text-(--text) transition-opacity duration-200">{loadingText}</p>
			{:else}
				<p class="relative animate-[fade-up_500ms_350ms_ease_both] overflow-hidden text-xl font-bold text-(--text)">
					TAP TO START
					<span class="absolute inset-y-0 w-[40%] animate-[glint-sweep_2s_ease-in-out_infinite] bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.45)_50%,transparent_100%)]"></span>
				</p>
			{/if}
		</div>
	</div>
{/if}

<style>
	.loading-fill {
		transition-property: width;
		transition-duration: 80ms;
		transition-timing-function: linear;
	}

	.fill-bar {
		background: linear-gradient(
			to bottom,
			var(--correct-frame-highlight) 0px,
			var(--correct-frame-highlight) 2px,
			var(--correct-frame-fill) 2px,
			var(--correct-frame-fill) 100%
		);
	}

	.glint {
		position: absolute;
		inset-block: 0;
		width: 35%;
		background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.5) 50%, transparent 100%);
		animation: glint-sweep 2.5s ease-in-out infinite;
	}

	.outbreak-grunge {
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.70' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E");
		opacity: 0.22;
		mix-blend-mode: overlay;
	}

	.overgrown-burn {
		background:
			radial-gradient(ellipse at top right, rgba(5,10,5,0.45) 0%, transparent 40%),
			radial-gradient(ellipse at bottom left, rgba(8,12,5,0.50) 0%, transparent 42%),
			radial-gradient(ellipse at bottom right, rgba(10,18,5,0.60) 0%, transparent 48%);
	}

	.wizardry-burn {
		background:
			radial-gradient(ellipse at top right, rgba(15,8,40,0.50) 0%, transparent 40%),
			radial-gradient(ellipse at bottom left, rgba(18,8,45,0.55) 0%, transparent 42%),
			radial-gradient(ellipse at bottom right, rgba(25,12,55,0.65) 0%, transparent 48%);
	}

	.outbreak-burn {
		background:
			radial-gradient(ellipse at top right, rgba(60,20,5,0.45) 0%, transparent 40%),
			radial-gradient(ellipse at bottom left, rgba(70,25,8,0.50) 0%, transparent 42%),
			radial-gradient(ellipse at bottom right, rgba(90,35,10,0.60) 0%, transparent 48%);
	}

	.outbreak-noise {
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='turbulence' baseFrequency='0.15' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='400' height='400' filter='url(%23n)'/%3E%3C/svg%3E");
		opacity: 0.12;
		mix-blend-mode: multiply;
	}

	.overgrown-grunge {
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E");
		opacity: 0.09;
		mix-blend-mode: overlay;
	}

	@keyframes glint-sweep {
		0%   { left: -35%; opacity: 0; }
		15%  { opacity: 1; }
		85%  { opacity: 1; }
		100% { left: 120%; opacity: 0; }
	}

	.noir-grain {
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E");
		opacity: 0.08;
		mix-blend-mode: overlay;
		animation: noir-flicker 0.18s steps(2) infinite;
	}
	.noir-scratches {
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Cline x1='40' y1='0' x2='38' y2='400' stroke='%23ffffff' stroke-width='1' opacity='0.25'/%3E%3Cline x1='112' y1='0' x2='114' y2='400' stroke='%23ffffff' stroke-width='0.6' opacity='0.14'/%3E%3Cline x1='198' y1='0' x2='196' y2='400' stroke='%23ffffff' stroke-width='1' opacity='0.20'/%3E%3Cline x1='268' y1='0' x2='266' y2='400' stroke='%23ffffff' stroke-width='0.5' opacity='0.12'/%3E%3Cline x1='342' y1='0' x2='344' y2='400' stroke='%23ffffff' stroke-width='1' opacity='0.18'/%3E%3C/svg%3E");
		background-size: 400px 100%;
		mix-blend-mode: screen;
		opacity: 0.5;
		animation: noir-scratch-drift 1.2s steps(3) infinite;
	}
	.noir-dust {
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='320'%3E%3Ccircle cx='25' cy='40' r='1.2' fill='%23ffffff' opacity='0.25'/%3E%3Ccircle cx='80' cy='150' r='0.7' fill='%23ffffff' opacity='0.20'/%3E%3Ccircle cx='160' cy='90' r='1' fill='%23000000' opacity='0.25'/%3E%3Ccircle cx='240' cy='200' r='0.9' fill='%23ffffff' opacity='0.25'/%3E%3Ccircle cx='200' cy='260' r='0.6' fill='%23000000' opacity='0.25'/%3E%3Ccircle cx='60' cy='280' r='1.1' fill='%23ffffff' opacity='0.22'/%3E%3Ccircle cx='280' cy='50' r='0.8' fill='%23ffffff' opacity='0.25'/%3E%3Ccircle cx='110' cy='220' r='0.5' fill='%23000000' opacity='0.25'/%3E%3Ccircle cx='180' cy='30' r='0.6' fill='%23ffffff' opacity='0.20'/%3E%3Ccircle cx='300' cy='160' r='0.9' fill='%23ffffff' opacity='0.20'/%3E%3C/svg%3E");
		mix-blend-mode: overlay;
		opacity: 0.6;
		animation: noir-dust-shift 0.6s steps(2) infinite;
	}
	.noir-burn {
		background:
			radial-gradient(ellipse at top right, rgba(0,0,0,0.45) 0%, transparent 45%),
			radial-gradient(ellipse at bottom left, rgba(0,0,0,0.45) 0%, transparent 45%),
			radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.55) 100%);
	}
	@keyframes noir-flicker {
		0%, 100% { opacity: 0.08; }
		50%      { opacity: 0.12; }
	}
	@keyframes noir-scratch-drift {
		0%   { transform: translateX(0); }
		33%  { transform: translateX(-30px); }
		66%  { transform: translateX(20px); }
		100% { transform: translateX(0); }
	}
	@keyframes noir-dust-shift {
		0%   { transform: translate(0, 0); }
		50%  { transform: translate(-12px, 8px); }
		100% { transform: translate(0, 0); }
	}
</style>
